import React, { memo, useCallback, useId } from 'react';
import CollapseChevron from './CollapseChevron';
import { useCollapsiblePanelState } from './useCollapsiblePanelState';

export type CollapsiblePanelProps = {
  /** Stable id used for localStorage persistence within storageScope. */
  id: string;
  /** Namespace for persistence keys (e.g. page name). */
  storageScope: string;
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  defaultExpanded?: boolean;
  /** Controlled expanded state (optional). When set, use with onExpandedChange. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
  /** When true, children are not mounted while collapsed (use for heavy tables). */
  unmountWhenCollapsed?: boolean;
  className?: string;
  children: React.ReactNode;
};

function CollapsiblePanelInner({
  id,
  storageScope,
  title,
  subtitle,
  badge,
  actions,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  disabled = false,
  loading = false,
  unmountWhenCollapsed = true,
  className = '',
  children,
}: CollapsiblePanelProps) {
  const reactId = useId();
  const panelId = `panel-${id}-${reactId.replace(/:/g, '')}`;
  const [internalExpanded, setInternalExpanded] = useCollapsiblePanelState(
    storageScope,
    id,
    defaultExpanded,
  );
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const setExpanded = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      const value = typeof next === 'function' ? next(isControlled ? expandedProp! : internalExpanded) : next;
      if (isControlled) {
        onExpandedChange?.(value);
      } else {
        setInternalExpanded(value);
      }
    },
    [expandedProp, internalExpanded, isControlled, onExpandedChange, setInternalExpanded],
  );

  const toggle = useCallback(() => {
    if (disabled) return;
    setExpanded((prev) => !prev);
  }, [disabled, setExpanded]);

  const onHeaderKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggle();
      }
    },
    [disabled, toggle],
  );


  return (
    <section
      className={[
        'collapsible-panel',
        expanded ? 'collapsible-panel--expanded' : 'collapsible-panel--collapsed',
        disabled ? 'collapsible-panel--disabled' : '',
        loading ? 'collapsible-panel--loading' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-panel-id={id}
    >
      <div className="collapsible-panel__header">
        <button
          type="button"
          className="collapsible-panel__trigger"
          id={panelId}
          aria-expanded={expanded}
          aria-controls={`${panelId}-body`}
          disabled={disabled}
          onClick={toggle}
          onKeyDown={onHeaderKeyDown}
        >
          <CollapseChevron expanded={expanded} />
          <span className="collapsible-panel__titles">
            <span className="collapsible-panel__title">{title}</span>
            {subtitle ? <span className="collapsible-panel__subtitle">{subtitle}</span> : null}
          </span>
          {badge != null && badge !== false && (
            <span className="collapsible-panel__badge">{badge}</span>
          )}
        </button>
        {actions ? <div className="collapsible-panel__actions">{actions}</div> : null}
      </div>

      {(expanded || !unmountWhenCollapsed) && (
        <div
          id={`${panelId}-body`}
          className="collapsible-panel__body"
          role="region"
          aria-labelledby={panelId}
          aria-hidden={!expanded}
        >
          <div className="collapsible-panel__body-inner">
            {loading ? (
              <div className="collapsible-panel__loading" role="status">
                <span className="spinner" aria-hidden />
                Loading…
              </div>
            ) : expanded || !unmountWhenCollapsed ? (
              children
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}

const CollapsiblePanel = memo(CollapsiblePanelInner);
CollapsiblePanel.displayName = 'CollapsiblePanel';

export default CollapsiblePanel;
